/**
 * Other coaches' names and avatars.
 *
 * `profiles` RLS lets a coach read only their own row, so a direct select (or
 * a `profile:profiles!user_id(...)` join) of anyone else comes back empty,
 * and every surface that did that showed "Coach" for every other person
 * (masterclass discussions, the org portal). fetch_profile_display_by_ids is
 * the SECURITY DEFINER read Community already uses: public display fields
 * only, no subscription filter, so lapsed or founder accounts still resolve.
 * public_profiles (chat's) is the fallback if it's missing, then the direct
 * select, which still works for admins.
 */
import { supabase } from "../../supabaseClient";

export const DISPLAY_COLS = "id, first_name, last_name, handle, avatar_url";

/** { [id]: { id, first_name, last_name, handle, avatar_url, role, is_bam_coach, cert_status } } */
export async function fetchDisplayProfiles(ids) {
  const want = [...new Set((ids || []).filter(Boolean))];
  if (!want.length) return {};
  const toMap = (rows) => Object.fromEntries((rows || []).map((p) => [p.id, p]));
  const a = await supabase.rpc("fetch_profile_display_by_ids", { p_ids: want });
  if (!a.error) return toMap(a.data);
  const b = await supabase.rpc("public_profiles", { p_ids: want });
  if (!b.error) return toMap(b.data);
  const { data } = await supabase.from("profiles").select(DISPLAY_COLS).in("id", want);
  return toMap(data);
}

const named = (p) => !!(p && (p.first_name || p.last_name || p.handle));

/**
 * Rows that carry a joined `profile` (which RLS empties for anyone but the
 * viewer) get the public display fields filled in. Whatever the join did
 * return is kept, so an admin viewer still sees fields like is_admin.
 */
export async function fillProfiles(rows, idKey = "user_id", key = "profile") {
  const list = rows || [];
  const missing = list.filter((r) => !named(r[key])).map((r) => r[idKey]);
  if (!missing.length) return list;
  const map = await fetchDisplayProfiles(missing);
  return list.map((r) => (named(r[key]) || !map[r[idKey]] ? r : { ...r, [key]: { ...(r[key] || {}), ...map[r[idKey]] } }));
}
